import React from "react";
import {
  TouchableOpacity,
  ActivityIndicator,
  AccessibilityRole,
  AccessibilityState,
  StyleSheet,
  ViewStyle,
  TextStyle,
  StyleProp,
} from "react-native";
import { Text } from "./Text";
import { radius, spacing, fonts, fontSizes, shadows } from "~/styles/tokens";
import { useTheme } from "~/styles/theme";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps {
  label: string;
  onPress: () => void;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  fullWidth?: boolean;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityState?: AccessibilityState;
}

export const Button: React.FC<ButtonProps> = ({
  label,
  onPress,
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  style,
  fullWidth = false,
  accessibilityLabel,
  accessibilityRole = "button",
  accessibilityState,
}) => {
  const { colors } = useTheme();
  const isDisabled = disabled || loading;

  const variantStyle: ViewStyle =
    variant === "primary"
      ? { backgroundColor: colors.primary, ...shadows.sm }
      : variant === "secondary"
        ? { backgroundColor: colors.surface, borderColor: colors.border }
        : variant === "danger"
          ? { backgroundColor: colors.danger }
          : { backgroundColor: "transparent" };

  const labelColor =
    variant === "primary"
      ? colors.primaryForeground
      : variant === "danger"
        ? colors.primaryForeground
        : colors.primary;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isDisabled}
      activeOpacity={0.85}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole={accessibilityRole}
      accessibilityState={{ disabled: isDisabled, ...accessibilityState }}
      style={[
        styles.base,
        variantStyle,
        styles[size],
        fullWidth && styles.fullWidth,
        isDisabled && styles.disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={labelColor} />
      ) : (
        <Text
          style={[styles.label, { color: labelColor }, labelSizeStyle[size]]}
        >
          {label}
        </Text>
      )}
    </TouchableOpacity>
  );
};

const labelSizeStyle: Record<Size, TextStyle> = {
  sm: { fontSize: fontSizes.sm },
  md: { fontSize: fontSizes.md },
  lg: { fontSize: fontSizes.md },
};

const styles = StyleSheet.create({
  base: {
    borderRadius: radius.full,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    borderWidth: 1.5,
    borderColor: "transparent",
  },
  sm: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
    minHeight: 36,
  },
  md: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 3,
    minHeight: 48,
  },
  lg: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    minHeight: 54,
  },
  fullWidth: {
    alignSelf: "stretch",
  },
  disabled: {
    opacity: 0.45,
  },
  label: {
    fontFamily: fonts.subheading,
    letterSpacing: 0.2,
  },
});
